import type { FloorAssemblyHashParameters } from "building-types/floors/floorAssemblyTypes";
import type { HalfEdgeToFloorPath } from "building-types/floors/floorSystemTypes";
import type { Floor } from "building-types/floors/floorTypes";
import { type SolidFloor } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { SlabType } from "building-types/foundations/foundationEnums";
import type {
  FoundationExtrusionSize,
  FoundationSuperSystem,
  GeneratedFoundationSuperSystem,
  HalfEdgeToFoundationPath,
  Slab,
} from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { FoundationSystemSettings, SlabSettings } from "building-types/settings/foundationSettingsTypes";
import type { Table } from "common/data/table";
import { enumValues } from "common/utils/enum";
import {
  mapDataToEdgeIndexMap,
  edgeDataArrRemap,
  polygonAddClosingSegmentDefaultData,
  polygonAdjustWindingData,
  polygonInnerRingDefaultDataFill,
} from "doc/provenance/edgeDataTools";
import {
  polyLine2EdgeDataNewFromHalfEdgeArray,
  arrayEdgeDataUnion,
  defaultHalfEdgeData,
  polyLine2EdgeDataCopy,
} from "doc/provenance/halfEdgeDataTools";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { classifyContainmentPolyLine2PolyLine2Fast, PolyLine2Containment } from "geom/containment";
import { WindingDir } from "geom/core";
import { offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import { polygonSubtract } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2WindingDir } from "geom/polyLine2";
import { polygonClose, polygonCopy, polygonCopySimplify, polygonSetCCW } from "geom/polyLine2Polygon";
import { type PolyLine2, type PolyLine2Polygon } from "geom/polyLine2Types";
import { isSolidFloor } from "../floor/floor";
import { makeSlabSize, slabEdgeSubSystemExteriorOffset, slabEdgeSubSystemOffset, slabNew } from "./slab";
import { slabTypeAtRoomClass } from "./slabUtils";

interface MakeFoundationSuperSystemOptions {
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly planarGraph: PolygonPlanarGraph;
  readonly foundationSettings: FoundationSystemSettings;
  readonly foundationComponents: Table<FoundationComponentEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
  readonly issues: BuildingGenerationIssue[];
  readonly roomClassFloors: Floor[][];
  readonly floorPaths: HalfEdgeToFloorPath;
}

export function makeFoundationSuperSystemData(
  opts: MakeFoundationSuperSystemOptions,
  foundationPaths: HalfEdgeToFoundationPath,
  id: string,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
): GeneratedFoundationSuperSystem {
  const { level, foundationSettings } = opts;

  const levelId = level.id;
  const foundationSuperSystem = makeFoundationSuperSystem(foundationPaths, opts);
  return {
    ...buildingEntityBaseNew("FOUNDATION_SUPER_SYSTEM", id, entityHandleForSelectingReportingUnit),
    levelId,
    settings: foundationSettings,
    superSystem: foundationSuperSystem,
  };
}

export function makeFoundationSuperSystem(
  foundationPaths: HalfEdgeToFoundationPath,
  opts: MakeFoundationSuperSystemOptions,
): FoundationSuperSystem {
  const settings = opts.foundationSettings;
  const slabs = makeSlabs(foundationPaths, opts);

  return {
    type: settings.foundationSuperSystemType,
    slabs,
  };
}

function makeSlabs(foundationPaths: HalfEdgeToFoundationPath, opts: MakeFoundationSuperSystemOptions): Slab[] {
  const slabs: Slab[] = [];
  const slabFloors: SolidFloor[] = [];

  const {
    level,
    planarGraph: { planarGraph },
    foundationSettings,
    foundationComponents,
    roomClassFloors: floors,
  } = opts;

  // iterate room classes in order of priority
  for (const roomClass of enumValues(RoomClass, true)) {
    const roomClassFloors = floors[roomClass];
    if (!roomClassFloors) {
      continue;
    }

    const solidFloors = roomClassFloors.filter(isSolidFloor).sort((a: SolidFloor, b: SolidFloor) => {
      return b.floorElevation - a.floorElevation;
    });

    // slabs from higher priority room classes are subtracted
    // from the current slab edges
    const prioritySlabs = [...slabs];

    const roomClassSlabs: Slab[] = [];
    for (const floor of solidFloors) {
      if (slabTypeAtRoomClass(roomClass, foundationSettings) === SlabType.NONE) {
        // slabs need to be clipped by floorplates even if there is no slab
        slabFloors.push(floor);
        continue;
      }

      const floorSlabs = makeSolidFloorSlabs({
        planarGraph,
        floor,
        foundationPaths,
        floorPaths: opts.floorPaths,
        slabSettings: foundationSettings,
        foundationComponents,
        prioritySlabs: prioritySlabs,
        priorityFloors: slabFloors,
        levelId: level.id,
      });

      // these slabs should be subtracted from
      // slabs generated afterwards in the same room class
      prioritySlabs.push(...floorSlabs);

      // add the slabs to the output
      roomClassSlabs.push(...floorSlabs);
    }

    slabs.push(...roomClassSlabs);
  }

  // should optimize this
  for (let i = 0; i < slabs.length; i++) {
    const int = slabs[i].planProfile[0];
    for (let j = 0; j < slabs.length; j++) {
      if (i === j || slabs[j].planProfile.length <= 1) {
        continue;
      }
      const ext = slabs[j].exteriorProfile[0];
      if (classifyContainmentPolyLine2PolyLine2Fast(int, ext) !== PolyLine2Containment.OUTSIDE) {
        slabs[i].isSlabDepression = true;
        break;
      }
    }
  }

  return slabs;
}

function makeSolidFloorSlabs({
  planarGraph,
  floor,
  foundationPaths,
  floorPaths,
  slabSettings,
  foundationComponents,
  prioritySlabs = [],
  priorityFloors = [],
  levelId,
}: {
  planarGraph: PlanarGraph;
  floor: SolidFloor;
  foundationPaths: HalfEdgeToFoundationPath;
  floorPaths: HalfEdgeToFloorPath;
  slabSettings: SlabSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  prioritySlabs?: Slab[];
  priorityFloors?: SolidFloor[];
  levelId: string;
}): Slab[] {
  const { roomClass, floorPlate, floorElevation } = floor;
  const { perimeters: floorLoops } = floorPlate;

  if (slabTypeAtRoomClass(roomClass, slabSettings) === SlabType.NONE) {
    return [];
  }

  const slabSize = makeSlabSize({
    roomClass,
    slabSettings,
    floorElevation,
  });

  if (slabSize.depth <= 0) {
    return [];
  }

  const slabs: Slab[] = [];
  const slabWindings: WindingDir[] = [];
  const slabPlanProfiles: PolyLine2[] = [];
  const slabExteriorProfiles: PolyLine2[] = [];
  const exteriorProfileHalfEdges: EdgeData[][][] = [];
  const planProfileHalfEdges: EdgeData[][][] = [];

  for (let i = 0; i < floorLoops.faceHalfEdges.length; i++) {
    const floorLoop = floorLoops.faceHalfEdges[i];
    const { planProfile, planProfileHalfEdges: slabPlanProfileHalfEdges } = slabPlanProfile({
      roomClass,
      floorLoop,
      planarGraph,
      foundationPaths,
      floorPaths,
      slabSettings,
      foundationComponents,
      levelId,
    });
    planProfileHalfEdges.push(slabPlanProfileHalfEdges);

    const { exteriorProfile, exteriorProfileHalfEdges: slabExteriorProfileHalfEdges } = slabExteriorProfile({
      roomClass,
      floorLoop,
      planarGraph,
      foundationPaths,
      floorPaths,
      slabSettings,
      foundationComponents,
      levelId,
    });

    exteriorProfileHalfEdges.push(slabExteriorProfileHalfEdges);

    slabWindings.push(polyLine2WindingDir(planProfile));
    slabPlanProfiles.push(planProfile);
    slabExteriorProfiles.push(exteriorProfile);
  }

  for (let i = 0; i < floorLoops.faceHalfEdges.length; i++) {
    const floorLoop = floorLoops.faceHalfEdges[i];
    if (slabWindings[i] === WindingDir.CW) {
      continue;
    }

    const exteriorProfile = slabExteriorProfiles[i];
    const planProfile = slabPlanProfiles[i];
    const slabHoles = slabPlanProfiles.filter((p, i) => {
      return (
        slabWindings[i] === WindingDir.CW &&
        classifyContainmentPolyLine2PolyLine2Fast(planProfile, p) !== PolyLine2Containment.INSIDE
      );
    });

    slabs.push(
      makeSlab({
        halfEdges: floorLoop,
        roomClass,
        size: slabSize,
        exteriorProfile: [exteriorProfile, ...slabHoles],
        planProfile: [planProfile, ...slabHoles],
        prioritySlabs,
        priorityFloors,
        exteriorProfileHalfEdges: polygonInnerRingDefaultDataFill(
          exteriorProfileHalfEdges[i],
          slabHoles,
          polyLine2EdgeDataCopy,
          defaultHalfEdgeData(levelId),
        ),
        planProfileHalfEdges: polygonInnerRingDefaultDataFill(
          planProfileHalfEdges[i],
          slabHoles,
          polyLine2EdgeDataCopy,
          defaultHalfEdgeData(levelId),
        ),
        levelId,
        hashParams: floor.hashParams,
      }),
    );
  }

  return slabs;
}

function slabPlanProfile({
  roomClass,
  floorLoop,
  planarGraph,
  foundationPaths,
  floorPaths,
  slabSettings,
  foundationComponents,
  levelId,
}: {
  roomClass: RoomClass;
  floorLoop: number[];
  planarGraph: PlanarGraph;
  foundationPaths: HalfEdgeToFoundationPath;
  floorPaths: HalfEdgeToFloorPath;
  slabSettings: SlabSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  levelId: string;
}) {
  const floorPerimeter = planarGraphHalfEdgeStripToPolyLine2(planarGraph, floorLoop);
  const { pl: planProfile, edgeMapping } = offsetPolyLine2VariableStep(
    floorPerimeter,
    floorLoop.map((halfEdge) => {
      const foundationPath = foundationPaths[halfEdge] ?? foundationPaths[planarGraphAdjacentHalfEdge(halfEdge)];
      const floorPath = floorPaths[halfEdge] ?? floorPaths[planarGraphAdjacentHalfEdge(halfEdge)];
      if (!floorPath) {
        return 0;
      }
      // Slab extents follow floor geometry; missing foundation paths (e.g. per-room disable) must
      // not collapse offsets to the room outline.
      if (!foundationPath) {
        return floorPath.floorOffset;
      }
      //note if they are not from the same planar graph, then we can't offset the slab as they are not connected
      //via the graph relationships. It is still possible that the these paths are overlapping but
      //that is not something we have decided to determine here directly. It must be resolved at a higher level
      //using the graphs.
      if (foundationPath.planarGraphId !== floorPath.planarGraphId) {
        return 0;
      }
      return slabEdgeSubSystemOffset(roomClass, foundationPath, floorPath, slabSettings, foundationComponents);
    }),
    true,
  );

  return {
    planProfile,
    planProfileHalfEdges: polyLine2EdgeDataNewFromHalfEdgeArray(
      levelId,
      mapDataToEdgeIndexMap(edgeMapping, floorLoop).map((he) => [he]),
    ),
  };
}

function slabExteriorProfile({
  roomClass,
  floorLoop,
  planarGraph,
  foundationPaths,
  floorPaths,
  slabSettings,
  foundationComponents,
  levelId,
}: {
  roomClass: RoomClass;
  floorLoop: number[];
  planarGraph: PlanarGraph;
  foundationPaths: HalfEdgeToFoundationPath;
  floorPaths: HalfEdgeToFloorPath;
  slabSettings: SlabSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  levelId: string;
}) {
  const floorPerimeter = planarGraphHalfEdgeStripToPolyLine2(planarGraph, floorLoop);
  const { pl: exteriorProfile, edgeMapping } = offsetPolyLine2VariableStep(
    floorPerimeter,
    floorLoop.map((halfEdge) => {
      const foundationPath = foundationPaths[halfEdge] ?? foundationPaths[planarGraphAdjacentHalfEdge(halfEdge)];
      const floorPath = floorPaths[halfEdge] ?? floorPaths[planarGraphAdjacentHalfEdge(halfEdge)];
      if (!floorPath) {
        return 0;
      }
      if (!foundationPath) {
        return floorPath.floorOffset;
      }
      if (foundationPath.planarGraphId !== floorPath.planarGraphId) {
        return 0;
      }
      return slabEdgeSubSystemExteriorOffset(roomClass, foundationPath, floorPath, slabSettings, foundationComponents);
    }),
    true,
  );

  return {
    exteriorProfile,
    exteriorProfileHalfEdges: polyLine2EdgeDataNewFromHalfEdgeArray(
      levelId,
      mapDataToEdgeIndexMap(edgeMapping, floorLoop).map((he) => [he]),
    ),
  };
}

export function makeSlab({
  roomClass,
  halfEdges,
  size,
  exteriorProfile,
  planProfile,
  prioritySlabs = [],
  priorityFloors = [],
  exteriorProfileHalfEdges,
  planProfileHalfEdges,
  levelId,
  hashParams,
}: {
  roomClass: RoomClass;
  halfEdges: number[];
  size: FoundationExtrusionSize;
  exteriorProfile: PolyLine2Polygon;
  planProfile: PolyLine2Polygon;
  prioritySlabs?: Slab[];
  priorityFloors?: SolidFloor[];
  exteriorProfileHalfEdges: EdgeData[][][];
  planProfileHalfEdges: EdgeData[][][];
  levelId: string;
  hashParams: Partial<FloorAssemblyHashParameters>;
}): Slab {
  const slabSubtractions: PolyLine2Polygon[] = [];

  for (const slab of prioritySlabs) {
    if (slab.roomClass === roomClass) {
      slabSubtractions.push(slab.planProfile);
    } else {
      slabSubtractions.push(slab.exteriorProfile);
    }
  }

  for (const floor of priorityFloors) {
    if (floor.floorElevation - floor.floorPlateThickness < size.top) {
      slabSubtractions.push(...floor.floorPlate.perimeterPolyLine2s.map((p) => [p]));
    }
  }

  const exteriorProfileCopy = polygonCopy(exteriorProfile);
  const planProfileCopy = polygonCopy(planProfile);

  if (slabSubtractions.length) {
    const exteriorProfileSubtracted = polygonSubtract([exteriorProfile, ...slabSubtractions]);
    exteriorProfile = exteriorProfileSubtracted[0] ?? exteriorProfile;
    const planProfileSubtracted = polygonSubtract([planProfile, ...slabSubtractions]);
    planProfile = planProfileSubtracted[0] ?? planProfile;
  }

  const exteriorProfileSimplified = polygonCopySimplify(exteriorProfile);
  const exteriorProfileHalfEdgesOut = edgeDataArrRemap(
    exteriorProfileCopy,
    exteriorProfileSimplified,
    exteriorProfileHalfEdges,
    arrayEdgeDataUnion,
    defaultHalfEdgeData(levelId),
  );

  const planProfileSimplified = polygonCopySimplify(planProfile);
  const planProfileHalfEdgesOut = edgeDataArrRemap(
    planProfileCopy,
    planProfileSimplified,
    planProfileHalfEdges,
    arrayEdgeDataUnion,
    defaultHalfEdgeData(levelId),
  );

  const { polygon: exteriorProfileClosed, requiredClose: exteriorRequiredClosingArr } =
    polygonClose(exteriorProfileSimplified);
  polygonAddClosingSegmentDefaultData(
    exteriorRequiredClosingArr,
    exteriorProfileHalfEdgesOut,
    defaultHalfEdgeData(levelId),
  );

  const { polygon: planProfileClosed, requiredClose: planProfileRequiredClosingArr } =
    polygonClose(planProfileSimplified);
  polygonAddClosingSegmentDefaultData(
    planProfileRequiredClosingArr,
    planProfileHalfEdgesOut,
    defaultHalfEdgeData(levelId),
  );

  let exteriorProfileSetArray: boolean[];
  ({ polygon: exteriorProfile, requiredSet: exteriorProfileSetArray } = polygonSetCCW(exteriorProfileClosed));
  polygonAdjustWindingData(exteriorProfileSetArray, exteriorProfileHalfEdgesOut);

  let planProfileSetArray: boolean[];
  ({ polygon: planProfile, requiredSet: planProfileSetArray } = polygonSetCCW(planProfileClosed));
  polygonAdjustWindingData(planProfileSetArray, planProfileHalfEdgesOut);

  return slabNew({
    roomClass,
    size,
    halfEdges,
    planProfile,
    exteriorProfile,
    exteriorProfileHalfEdges: exteriorProfileHalfEdgesOut,
    planProfileHalfEdges: planProfileHalfEdgesOut,
    hashParams,
  });
}
